import React from 'react'
import { useQuery } from '@apollo/client'
import { addHours } from 'date-fns'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  DatePicker,
  Input,
  RegularModal,
  Select,
} from '@/components_deprecated'
import { ALL_USER_EQUIPMENT_TYPES_QUERY } from '@/modules/users/queries'
import { Field, Form } from '..'
import type {
  EditUserEquipmentForm,
  EditUserEquipmentPromptResolve,
  UserEquipmentForSettings,
} from './types'

interface EditUserEquipmentPromptProps {
  userEquipment?: UserEquipmentForSettings
  equipmentTypes?: Array<{ id: string; name: string }>
  onSubmit: PromptResolve<EditUserEquipmentPromptResolve | null>
}

export const EditUserEquipmentPrompt: React.FC<
  EditUserEquipmentPromptProps
> = ({ userEquipment, equipmentTypes: propEquipmentTypes, onSubmit }) => {
  const translations = useTranslate({
    createEquipment: 'settings.create-equipment',
    editEquipment: 'settings.edit-equipment',
    selectEquipmentType: 'settings.select-equipment-type',

    name: 'common.name',
    equipmentType: 'equipment.equipment-type',
    assignedDate: 'equipment.assigned-date',
    returnDate: 'equipment.return-date',
    useReturnDate: 'equipment.use-return-date',

    save: 'common.save',
    cancel: 'common.cancel',

    validation: {
      mustHaveName: 'settings.validation.equipment.must-have-name',
      mustSelectType: 'settings.validation.equipment.must-select-type',
    },
  })

  const editing = typeof userEquipment !== 'undefined'

  const assignedDate = userEquipment?.assignedDate
    ? new Date(userEquipment.assignedDate)
    : new Date()
  const returnDate = userEquipment?.returnDate
    ? new Date(userEquipment.returnDate)
    : null

  const { data: equipmentTypesData, loading: equipmentTypesLoading } = useQuery(
    ALL_USER_EQUIPMENT_TYPES_QUERY,
    {
      skip: !!propEquipmentTypes,
      onError: error => {
        console.error('Failed to fetch equipment types:', error)
      },
    }
  )

  const equipmentTypes =
    propEquipmentTypes ||
    equipmentTypesData?.allUserEquipmentTypes.edges.map(
      (edge: any) => edge.node
    ) ||
    []

  const {
    formValues: form,
    formValid,
    formErrors: errors,
    formChangeHandler: handler,
    updateForm,
  } = useForm<EditUserEquipmentForm>({
    values: {
      name: userEquipment?.name ?? '',
      equipmentType: userEquipment?.equipmentType?.id ?? '',
      assignedDate: assignedDate,
      useReturnDate: !!returnDate,
      returnDate: returnDate,
    },
    validators: {
      equipmentType: (val: string | null) =>
        !val ? translations.validation.mustSelectType : null,
    },
  })

  return (
    <RegularModal
      title={
        editing
          ? `${translations.editEquipment}: ${userEquipment?.name ?? ''}`
          : translations.createEquipment
      }
      width="480px"
      submitDisabled={!formValid || equipmentTypesLoading}
      submitText={translations.save}
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit({ form })}
    >
      <Form>
        <Field>
          <label>{translations.name}</label>
          <Input
            value={form.name}
            error={errors.name}
            fullWidth
            autoFocus
            grayed
            onChange={handler('name')}
          />
        </Field>

        <Field>
          <label>{translations.equipmentType}</label>
          <Select
            value={form.equipmentType}
            error={errors.equipmentType}
            fullWidth
            grayed
            loading={equipmentTypesLoading}
            onChange={(_, option) => {
              const value = option?.value || ''
              updateForm({ equipmentType: value })
            }}
            options={equipmentTypes.map(
              (type: { id: string; name: string }) => ({
                value: type.id,
                label: type.name,
              })
            )}
            placeholder={translations.selectEquipmentType}
          />
        </Field>

        <Field>
          <label>{translations.assignedDate}</label>
          <DatePicker
            value={form.assignedDate ?? new Date()}
            fullWidth
            clearable
            grayed
            onChange={value => {
              const newDate = value ? addHours(value, 9) : null
              updateForm({ assignedDate: newDate })
            }}
          />
        </Field>

        <Field>
          <label>{translations.useReturnDate}</label>
          <Checkbox
            checked={form.useReturnDate}
            onChange={value => {
              updateForm({ useReturnDate: value })
            }}
            inline
            grayed
          />
        </Field>
        {form.useReturnDate && (
          <Field>
            <label>{translations.returnDate}</label>
            <DatePicker
              value={form.returnDate ?? new Date()}
              fullWidth
              clearable
              grayed
              onChange={value => {
                const newDate = value ? addHours(value, 9) : null
                updateForm({ returnDate: newDate })
              }}
            />
          </Field>
        )}
      </Form>
    </RegularModal>
  )
}
